/**
 * What a dish is made of, for the kitchen card — `FRONTEND.md` §9.55, `BACKEND.md` §10A.15.
 *
 * *"tak, ako je popis pri vlastnej pizze vidieť, by malo byť vidieť u každého produktu z toho, čo je
 * nastavené ako RECEPT"* (owner, fourth round).
 *
 * **A recipe does not replace the description the screen already draws, and the menu is why.**
 * Measured on the restored data: 87 of 173 products have a recipe, 95 have a `kitchenDescription`,
 * and the two overlap on only **44**. So 43 products have a recipe and no prose — those are the ones
 * showing a cook a bare name today — and 51 have prose and no recipe. Reading either source alone
 * covers about half the menu; preferring the recipe and falling back to the prose covers **138**.
 *
 * Where both exist they say the same thing in different words, and the recipe is the better of the
 * two: *Beef bowl* is `Ľadový šalát, Trhané hovädzie 150g, …` as prose and `Ľadový šalát 50 g,
 * Trhané hovädzie 150 g, …` as a recipe. That is why the recipe wins rather than being appended —
 * showing both would print the same list twice.
 *
 * Pure, and separate from the card for the reason `item-name.ts` is: which of two sources a cook is
 * shown is a rule, and a rule that only exists inside a component is a rule nobody can check.
 */

export interface RecipeIngredient {
  name: string
  quantity?: number | null
  unit?: string | null
}

/** A row of `GET /kitchen/recipes`, read defensively because it arrives untyped from the query. */
export interface RecipeRow {
  productId?: number
  ingredients?: { name?: string; quantity?: number | null; unit?: string | null }[] | null
}

/**
 * Product id → its ingredients.
 *
 * §10A.15 already answers one row per product, so this does not have to break a tie. It still keeps
 * the **first** row for a product rather than the last, so that if that guarantee is ever relaxed
 * this side behaves the same way the server does instead of silently disagreeing with it.
 *
 * A recipe with no usable ingredients is dropped rather than stored empty: the card asks *is there a
 * recipe*, and an empty list that answers yes would suppress the prose fallback for nothing.
 */
export function buildRecipeIndex(
  rows: readonly RecipeRow[],
  nameByProductId: Readonly<Record<number, string>> = {},
): Record<number, RecipeIngredient[]> {
  const byProduct: Record<number, RecipeIngredient[]> = {}
  for (const row of rows) {
    if (row.productId === undefined || row.productId === null) continue
    if (byProduct[row.productId] !== undefined) continue
    const ingredients = (row.ingredients ?? [])
      .filter(
        (ingredient): ingredient is { name: string; quantity?: number | null; unit?: string | null } =>
          typeof ingredient?.name === 'string' && ingredient.name.trim() !== '',
      )
      .map((ingredient) => ({
        name: ingredient.name.trim(),
        quantity: ingredient.quantity ?? null,
        unit: ingredient.unit ?? null,
      }))
    if (ingredients.length === 0) continue
    if (restatesTheDish(ingredients, nameByProductId[row.productId])) continue
    byProduct[row.productId] = ingredients
  }
  return byProduct
}

/**
 * Whether a "recipe" is just the dish written down again — measured, and it is 25 products.
 *
 * `Pepsi Cola 0,33l` has a recipe whose single ingredient is *Pepsi Cola 0,33l, 1 ks*. That is a
 * stock link wearing a recipe's clothes: it tells the warehouse what to decrement and tells a cook
 * nothing at all. **Of the 43 products that have a recipe and no prose description, 25 are this** —
 * so drawing every recipe would have put a line of pure noise on a wall-mounted screen for products
 * that correctly showed nothing before, which is worse than the gap it was meant to close.
 *
 * Only a single-ingredient recipe can be self-restating; a real one names other things.
 */
function restatesTheDish(
  ingredients: readonly RecipeIngredient[],
  productName: string | undefined,
): boolean {
  if (ingredients.length !== 1 || productName === undefined) return false
  return ingredients[0]!.name.trim().toLowerCase() === productName.trim().toLowerCase()
}

/**
 * `150 g`, not `150.000 g`.
 *
 * `recipe_ingredients.quantity` is `numeric` and answers three decimals for every whole number, so
 * an unformatted recipe reads `1.000 ks` on every line — which shows a cook the column type rather
 * than the recipe. Fractions that matter are kept: `0.5 l` stays `0.5 l`.
 *
 * A quantity of nothing is not printed as `0`. The row exists, the amount does not, and *Oregano*
 * alone is a truer line than *0 Oregano*.
 */
export function formatAmount(ingredient: RecipeIngredient): string | null {
  const { quantity, unit } = ingredient
  if (quantity === null || quantity === undefined || !Number.isFinite(quantity)) {
    return unit ? unit : null
  }
  const rounded = Math.round(quantity * 1000) / 1000
  const amount = String(rounded)
  return unit ? `${amount} ${unit}` : amount
}
